import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray } from '@angular/forms';
import moment from 'moment';
import { DrupalRESTService } from 'src/app/services/drupal-rest.service';
import { CurrencyService } from '../../../services/currency.service';
import { MatExpansionPanel } from '@angular/material/expansion';
import { TAX_TYPES } from '../../../constants/tax.constants';
import { LoggerService } from '../../../services/logger.service';

@Component({
  selector: 'app-tax-configuration',
  templateUrl: './tax-configuration.component.html',
  styleUrls: ['./tax-configuration.component.css']
})
export class TaxConfigurationComponent implements OnInit {
  taxConfigurationForm: FormGroup;
  actionType: 'create' | 'edit' = 'create';
  /** Starts true so the empty accordion never paints before the GET lands. */
  showProgressSpinner = true;
  successMessage: string;
  errorMessage: string;

  calculationMethods = [
    { value: 'PERCENTAGE', label: 'Percentage (%)' },
    { value: 'FLAT_RATE', label: 'Fixed Amount' },
    { value: 'TIERED', label: 'Tiered Rates' }
  ];

  roundingRules = [
    { value: 'ROUND_UP', label: 'Round Up' },
    { value: 'ROUND_DOWN', label: 'Round Down' },
    { value: 'ROUND_NEAREST', label: 'Round to Nearest' },
    { value: 'NO_ROUNDING', label: 'No Rounding' }
  ];

  taxTypes = TAX_TYPES;

  constructor(
    private fb: FormBuilder,
    private drupalRESTService: DrupalRESTService,
    private currencyService: CurrencyService
  ) {}

  private parseBooleanValue(value: any): boolean {
    if (value === true || value === 'true') {
      return true;
    }

    const numericValue = Number(value);
    return numericValue === 1;
  }

  ngOnInit(): void {
    this.taxConfigurationForm = this.fb.group({
      taxConfigurations: this.fb.array([])
    });

    this.loadTaxConfigurations();
  }

  get taxConfigurations(): FormArray {
    return this.taxConfigurationForm.get('taxConfigurations') as FormArray;
  }

  createTaxConfigurationGroup(data?: any): FormGroup {
    return this.fb.group({
      id: [data?.id?.[0]?.value || ''],
      title: [data?.title?.[0]?.value || '', Validators.required],
      field_calculation_method: [data?.field_calculation_method?.[0]?.value || null],
      field_rounding_rule: [data?.field_rounding_rule?.[0]?.value || null],
      field_tax_status: [this.parseBooleanValue(data?.field_tax_status?.[0]?.value)],
      field_is_tax_inclusive: [this.parseBooleanValue(data?.field_is_tax_inclusive?.[0]?.value)],
      field_tax_type: [data?.field_tax_type?.[0]?.value || null],
      field_tax_value: [data?.field_tax_value?.[0]?.value || null],
      field_effective_tax_start_date: [data?.field_effective_tax_start_date?.[0]?.value || null],
      field_effective_tax_end_date: [data?.field_effective_tax_end_date?.[0]?.value || null],
    });
  }

  loadTaxConfigurations() {
    const configFieldName = 'field_tax_ref';
    this.drupalRESTService
      .httpGET(`/api_rest/v1/loadStudioConfig?config_field_name=${configFieldName}`)
      .subscribe(
        (response) => {
          LoggerService.log('Loaded tax configurations:', response);
          if (Array.isArray(response)) {
            response.forEach(taxConfig => {
              this.taxConfigurations.push(this.createTaxConfigurationGroup(taxConfig));
            });
          } else if (response) {
            this.taxConfigurations.push(this.createTaxConfigurationGroup(response));
          }
          this.actionType = 'edit';
          this.showProgressSpinner = false;
          LoggerService.log('Form structure:', this.taxConfigurationForm.value);
        },
        (error) => {
          LoggerService.error('Error loading tax configurations:', error);
          this.errorMessage = 'Error loading tax configurations.';
          this.showProgressSpinner = false;
        }
      );
  }

  addTaxConfiguration() {
    this.taxConfigurations.push(this.createTaxConfigurationGroup());
  }

  removeTaxConfiguration(index: number) {
    const configId = this.taxConfigurations.at(index).get('id').value;
    if (configId) {
      this.showProgressSpinner = true;
      const payload = { id: configId };
      this.drupalRESTService
        .httpPOST('/api_rest/v1/deleteStudioConfig', payload)
        .subscribe(
          (response) => {
            LoggerService.log('Tax configuration deleted successfully:', response);
            this.successMessage = 'Tax configuration deleted successfully.';
            this.taxConfigurations.removeAt(index);
            this.showProgressSpinner = false;
          },
          (error) => {
            LoggerService.error('Error deleting tax configuration:', error);
            this.errorMessage = 'Error deleting tax configuration.';
            this.showProgressSpinner = false;
          }
        );
    } else {
      this.taxConfigurations.removeAt(index);
    }
  }

  saveTaxConfigurations(postSave?: () => void) {
    const configFieldName = 'field_tax_ref';
    const formattedData = this.taxConfigurationForm.value.taxConfigurations.map(config => {
      const formattedConfig = { ...config };

      formattedConfig.field_tax_status = config.field_tax_status ? '1' : '0';
      formattedConfig.field_is_tax_inclusive = config.field_is_tax_inclusive ? '1' : '0';

      // Format the dates using moment.js in the expected format
      if (formattedConfig.field_effective_tax_start_date) {
        formattedConfig.field_effective_tax_start_date = moment(formattedConfig.field_effective_tax_start_date).format('YYYY-MM-DDTHH:mm:ss');
      }
      if (formattedConfig.field_effective_tax_end_date) {
        formattedConfig.field_effective_tax_end_date = moment(formattedConfig.field_effective_tax_end_date).format('YYYY-MM-DDTHH:mm:ss');
      }

      return formattedConfig;
    });

    const postData = {
      config_field_name: configFieldName,
      config_data: formattedData,
    };

    this.showProgressSpinner = true;
    this.successMessage = '';
    this.errorMessage = '';

    this.drupalRESTService
      .httpPOST('/api_rest/v1/saveStudioConfig', postData)
      .subscribe(
        (response) => {
          LoggerService.log('Tax configurations saved successfully:', response);
          this.successMessage = 'Tax configurations saved successfully.';
          this.showProgressSpinner = false;
          postSave?.();
        },
        (error) => {
          LoggerService.error('Error saving tax configurations:', error);
          this.handleError(error); // Handle the error more effectively
          this.showProgressSpinner = false;
        }
      );
  }

  triggerSave(panel: MatExpansionPanel) {
    this.saveTaxConfigurations(() => {
      panel.close();
    });
  }


  handleError(error: any) {
    // Parse and format the error message
    if (error.error && error.error.message) {
      this.errorMessage = `Error: ${error.error.message}`;
    } else if (error.status === 400) {
      this.errorMessage = 'Validation error. Please check your inputs and try again.';
    } else if (error.status === 500) {
      this.errorMessage = 'Server error. Please try again later.';
    } else {
      this.errorMessage = 'An unexpected error occurred. Please try again.';
    }
  }


  onSubmit() {
    this.saveTaxConfigurations();
  }

  onUpdate() {
    this.saveTaxConfigurations();
  }

  onCalculationMethodChange(index: number) {
    const taxConfig = this.taxConfigurations.at(index);
    const method = taxConfig.get('field_calculation_method').value;

    // Adjust the tax value field's placeholder based on the calculation method.
    taxConfig.get('field_tax_value').setValidators(method === 'PERCENTAGE' ? [Validators.required, Validators.min(0), Validators.max(100)] : [Validators.required, Validators.min(0)]);
    taxConfig.get('field_tax_value').updateValueAndValidity();
  }

  getTaxValueLabel(index: number): string {
    const method = this.taxConfigurations.at(index).get('field_calculation_method').value;
    return method === 'PERCENTAGE' ? 'Tax Percentage' : 'Tax Amount';
  }

  getTaxValuePlaceholder(index: number): string {
    const method = this.taxConfigurations.at(index).get('field_calculation_method').value;
    return method === 'PERCENTAGE' ? 'Enter percentage (0-100)' : 'Enter flat amount';
  }

  getHelpfulInfo(index: number): string {
    const taxConfig = this.taxConfigurations.at(index).value;

    const typeLabel = this.taxTypes.find(t => t.value === taxConfig.field_tax_type)?.label || 'No Type Set';
    const methodLabel = this.calculationMethods.find(m => m.value === taxConfig.field_calculation_method)?.label || 'No Method Set';
    const status = taxConfig.field_tax_status ? `<span class="status-active">Active</span>` : `<span class="status-inactive">Inactive</span>`;
    // Decimal fields come back at storage scale ("10.000"); display the number.
    const taxValue = taxConfig.field_tax_value !== null && taxConfig.field_calculation_method === 'PERCENTAGE'
      ? `${parseFloat(taxConfig.field_tax_value)}%`
      : taxConfig.field_tax_value !== null && taxConfig.field_calculation_method === 'FLAT_RATE'
      ? `${this.currencyService.format(taxConfig.field_tax_value)}`
      : '';

    let summary = `${typeLabel} • ${methodLabel}`;

    if (taxValue) {
      summary += ` • ${taxValue}`;
    }

    const inclusiveLabel = taxConfig.field_is_tax_inclusive ? 'Inclusive Pricing' : 'Exclusive Pricing';
    summary += ` • ${inclusiveLabel}`;

    summary += ` • ${status}`;

    return summary;
  }

  getDisplayLabel(value: string, type: 'method' | 'rule' | 'type'): string {
    if (type === 'method') {
      return this.calculationMethods.find(m => m.value === value)?.label || value;
    } else if (type === 'rule') {
      return this.roundingRules.find(r => r.value === value)?.label || value;
    } else if (type === 'type') {
      return this.taxTypes.find(t => t.value === value)?.label || value;
    }
    return value;
  }

  calculateTaxCollected(method: string, value: number): number {
    const baseAmount = 1000; // Assuming the base amount is 1000 as per your example
    let taxCollected = 0;

    switch (method) {
      case 'PERCENTAGE':
        taxCollected = (value / 1000) * baseAmount;
        break;
      case 'FLAT_RATE':
        taxCollected = value;
        break;
      case 'TIERED':
        // Implement tiered calculation logic if applicable
        taxCollected = 0; // Placeholder
        break;
      default:
        taxCollected = 0;
    }

    // Ensure taxCollected is rounded to two decimal places
    return Math.round(taxCollected * 100) / 100;
  }
}
